import type { IScannerControls } from "@zxing/browser";
import { CameraOutlined, PictureOutlined, QrcodeOutlined, ScanOutlined } from "@ant-design/icons";
import { Alert, Button, Input, Popover, Typography } from "antd";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppConfig } from "../../shared/config/AppConfigProvider";
import { normalizeVinInput } from "../../shared/utils/formatters";
import { repairOrdersApi } from "../repair-orders/repair-orders-api";

type Props = {
  repairOrderId?: string;
  compact?: boolean;
};

export function ScanButton({ repairOrderId, compact = false }: Props) {
  const { text } = useAppConfig();
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const controlsRef = useRef<IScannerControls | undefined>(undefined);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const busyRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [manualValue, setManualValue] = useState("");
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const stopCamera = () => {
    controlsRef.current?.stop();
    controlsRef.current = undefined;
    setScanning(false);
  };

  useEffect(() => () => controlsRef.current?.stop(), []);

  const routeScan = async (rawValue: string) => {
    const value = rawValue.trim();
    if (!value || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError(undefined);
    try {
      const roId = extractRepairOrderId(value);
      if (roId) {
        stopCamera();
        setOpen(false);
        navigate(`/repair-orders/${roId}/workspace`);
        return;
      }

      const vin = normalizeVinInput(value);
      if (vin.length === 17 && vin === value.toUpperCase().replace(/[^A-Z0-9]/g, "")) {
        const results = await repairOrdersApi.arrivalSearch(vin);
        const activeRepairEvent = results.repairEvents.find(item => item.vehicle.vin === vin);
        const vehicle = results.vehicles.find(item => item.vin === vin);
        stopCamera();
        setOpen(false);
        navigate(activeRepairEvent
          ? activeRepairEvent.attention.workspacePath
          : vehicle?.activeRepairEvent
            ? vehicle.activeRepairEvent.workspacePath
            : vehicle
              ? `/repair-orders/new?customerId=${vehicle.customerId}&vehicleId=${vehicle.id}&source=scan`
          : `/repair-orders/new?vin=${vin}&source=scan`);
        return;
      }

      if (/^RO[-:]/i.test(value)) {
        const results = await repairOrdersApi.arrivalSearch(value);
        const repairOrder = results.repairEvents.find(item => item.roNumber.toUpperCase() === value.toUpperCase());
        if (!repairOrder) throw new Error(text.repairOrderNotFound);
        stopCamera();
        setOpen(false);
        navigate(repairOrder.attention.workspacePath);
        return;
      }

      if (!repairOrderId) throw new Error(text.partScanWorkspaceOnly);
      stopCamera();
      setOpen(false);
      navigate(`/repair-orders/${repairOrderId}/workspace/parts?scan=${encodeURIComponent(value)}`);
    } catch (scanError) {
      setError(scanError instanceof Error ? scanError.message : text.scanNotRecognized);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const scanPhoto = async (file?: File) => {
    if (!file || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError(undefined);
    const objectUrl = URL.createObjectURL(file);
    try {
      const { BrowserMultiFormatReader } = await import("@zxing/browser");
      const result = await new BrowserMultiFormatReader().decodeFromImageUrl(objectUrl);
      busyRef.current = false;
      setBusy(false);
      await routeScan(result.getText());
    } catch {
      setError(text.scanPhotoUnreadable);
    } finally {
      URL.revokeObjectURL(objectUrl);
      busyRef.current = false;
      setBusy(false);
      if (photoInputRef.current) photoInputRef.current.value = "";
    }
  };

  const startCamera = async () => {
    setError(undefined);
    setScanning(true);
    await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
    if (!videoRef.current) return;
    try {
      const { BrowserMultiFormatReader } = await import("@zxing/browser");
      const reader = new BrowserMultiFormatReader();
      controlsRef.current = await reader.decodeFromConstraints(
        { audio: false, video: { facingMode: { ideal: "environment" } } },
        videoRef.current,
        result => {
          if (result) void routeScan(result.getText());
        }
      );
    } catch {
      setScanning(false);
      setError(text.cameraUnavailable);
    }
  };

  const content = <div className="scan-popover">
    <Typography.Title level={5}><ScanOutlined /> {text.scan}</Typography.Title>
    <Typography.Paragraph type="secondary">{text.scanHint}</Typography.Paragraph>
    {error && <Alert type="error" showIcon message={error} />}
    {scanning
      ? <div className="scan-camera"><video ref={videoRef} muted playsInline /><div className="scan-frame" /><Button onClick={stopCamera}>{text.stopCamera}</Button></div>
      : <Button block icon={<CameraOutlined />} onClick={() => void startCamera()}>{text.useCamera}</Button>}
    <input
      ref={photoInputRef}
      type="file"
      accept="image/*"
      hidden
      onChange={event => void scanPhoto(event.target.files?.[0])}
    />
    <Button block icon={<PictureOutlined />} loading={busy} onClick={() => photoInputRef.current?.click()}>
      {text.chooseScanPhoto}
    </Button>
    <div className="scan-divider"><span>{text.orUseScanner}</span></div>
    <Input
      autoFocus
      prefix={<QrcodeOutlined />}
      placeholder={text.scanInputPlaceholder}
      value={manualValue}
      onChange={event => setManualValue(event.target.value)}
      onPressEnter={() => void routeScan(manualValue)}
      suffix={<Button type="text" size="small" loading={busy} onClick={() => void routeScan(manualValue)}>{text.go}</Button>}
    />
  </div>;

  return <Popover
    trigger="click"
    placement="bottomRight"
    open={open}
    onOpenChange={next => { setOpen(next); if (!next) stopCamera(); }}
    content={content}
  >
    <Button size={compact ? "small" : "middle"} icon={<ScanOutlined />} aria-label={text.scan}>{compact ? null : text.scan}</Button>
  </Popover>;
}

function extractRepairOrderId(value: string): string | null {
  const match = value.match(/(?:repair-orders?|repair_order|ro_id)[/:=]([0-9a-f-]{36})/i);
  return match?.[1] ?? (/^[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(value) ? value : null);
}
